/**
 * Opportunities — L1 ranked briefing list (Lane 42; visual makeover Lane 47;
 * Linear rebuild Lane 207).
 *
 * Reads the latest-complete (or a selected) snapshot's findings from
 * GET /api/opportunities, ranked by GROSS headline DESC, joined to their
 * durable dispositions. Filters (family / status / search) are SERVER-side —
 * the hide-accepted-unless-badge default lives in the endpoint and is never
 * re-implemented here.
 *
 * DISPLAY ONLY: every figure renders verbatim from the response (formatting
 * via utils/opportunityDisplay). The UI never computes or re-derives a dollar.
 * The per-family chips render family_summaries VERBATIM (the backend asserts
 * the sums add exactly to total_opportunity — one source, plan §3.3A).
 *
 * Lane 207 (Linear direction — docs/design/mockups/opportunities-linear.html,
 * Ryan rev-1): presentation-only. The page moves onto the `.opp` page-scoped
 * Linear token/class layer (Opportunities.linear.css, light-only). Structure:
 *   - Header (title + snapshot picker + Regenerate).
 *   - Hero / summary strip (total + count + as-of + per-family chips).
 *   - Filter bar (family / status / debounced search — all server-side).
 *   - Ranked findings table — rank + SEPARATE material-number / name / supplier
 *     columns (the material number is a blue link into the material dossier,
 *     Wave-1 item 3), the Opportunity dollar rendered NUMBER-ONLY (the family
 *     word dropped from the cell; meaning stays in the Type column + hover
 *     title), a conditional inventory chip (volume-only), status; client-side
 *     "show all" fold. The Confidence column is dropped per rev-1 (confidence
 *     context stays on the L2 finding page). QA-1 (2026-07-22, Ryan-locked
 *     decision 1): the "Lead time" chip column is CUT — the lead-time reality
 *     story is owned by the Delivery Reliability surface now. The leadtime
 *     FAMILY row (Type "Lead time gap") and the L2 lead-time reality card are
 *     unaffected; only the list column is gone.
 *   - Three collapsed claim-free sections — "Already at your best price"
 *     (monitor / suppression) + "Best price no longer available" (monitor /
 *     Lane-352 price-gone, split out in Lane 355 so one heading is never false
 *     for the other flavor) + "Verify unit of measure" (verify). NO dollar
 *     renders anywhere in these sections (plan §1C/§1E; test-guarded).
 *
 * Flag gating: this page only mounts when OPPORTUNITIES_ENABLED is ON (route
 * registration in App.js + nav in Sidebar.js are both gated on the /me flag);
 * a 404 from the API (flag flipped OFF mid-session) renders a quiet
 * module-unavailable state, not an error.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { API_BASE } from '../config';
import {
  FAMILY_LABELS, DISPOSITION_LABELS,
  formatUsd, formatDate, triggerSourceLabel,
} from '../utils/opportunityDisplay';
import { pluralize } from '../utils/pluralize';
import './Opportunities.linear.css';

const FAMILY_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'volume', label: FAMILY_LABELS.volume },
  { value: 'dispersion', label: FAMILY_LABELS.dispersion },
  { value: 'same_day', label: FAMILY_LABELS.same_day },
  { value: 'leadtime', label: FAMILY_LABELS.leadtime },
  { value: 'spike_reversion', label: FAMILY_LABELS.spike_reversion },  // Lane 258
];

// '' = the server default view: undecided + pursuing + needs_data, plus
// accepted findings whose "changed since accepted" badge tripped.
const STATUS_OPTIONS = [
  { value: '', label: 'Active (accepted hidden)' },
  { value: 'pursuing', label: 'Pursuing' },
  { value: 'needs_data', label: 'Needs data' },
  { value: 'accepted', label: 'Accepted' },
];

// Client-side display fold (Lane 47 §3): rows shown before "Show all".
const FOLD_COUNT = 10;

// Lane 377: how the page rides out a Regenerate whose request outlives the
// proxy. `regenerate_opportunities` runs generation PLUS an inline per-finding
// Haiku prose upgrade synchronously, so behind a proxy with a ~100s ceiling the
// POST can time out (gateway/network error) even though the snapshot committed.
// On a POST failure we poll the latest snapshot for a bounded window before
// declaring failure — only a genuinely-failed regenerate leaves no newer one.
// Lane 431 raised the ceiling on MEASURED evidence. A real staging regenerate
// takes MINUTES, not seconds: cron_runs for the 2026-07-26 XMEK runs recorded
// duration_ms 463,595 and 463,757 — 7m43s and 7m44s for the GENERATION ALONE,
// with the inline per-finding Haiku prose pass still to run on top (up to a
// further 8 waves x 15s). The July-4 runs took 7s; the new step_change /
// spike_reversion / rebuilt-volume detector families are what changed. Against
// that, a 120s window guaranteed a FALSE "Could not generate a new briefing"
// while the server was still working and about to succeed — exactly what
// happened to both of Ryan's clicks. 15 minutes covers the measured duration
// with roughly 2x headroom.
const REGEN_POLL_MAX_MS = 900000;
// Poll cadence backs off so a 15-minute window does not hammer the L1 endpoint
// (each poll runs the full list query against a 3-connection pool). Responsive
// for the first minute, then slower: ~12 + ~42 = ~54 polls across the window
// instead of 180.
const REGEN_POLL_INTERVAL_MS = 5000;
const REGEN_POLL_INTERVAL_LATE_MS = 20000;
const REGEN_POLL_BACKOFF_AFTER_MS = 60000;

// Lane 431 — RYAN-DECIDES PLACEHOLDER. How old a briefing may be before the
// page says so out loud on age alone. 7 days is a placeholder chosen to match
// the weekly staging-refresh cadence, NOT a researched threshold; it is frozen
// here as a single named constant so changing it is a one-line edit. The
// stronger signal does not use it at all: a snapshot generated before the
// newest purchase data in the company's own records is provably out of date
// regardless of age, and warns on that basis.
const STALE_SNAPSHOT_MAX_AGE_DAYS = 7;
const MS_PER_DAY = 86400000;

// Lane 431 — staleness detection.
//
// The page ALREADY carried an "As of <date>" line and it did not register: a
// date on its own asks the reader to do the arithmetic and to know what "recent
// enough" means. So the page now states the CONCLUSION, in a warning strip
// above the numbers, with the reason attached.
//
// Two signals, strongest first:
//   'data' — the snapshot was generated BEFORE the newest purchase date the
//            company holds. This is a proof, not a heuristic, and needs no
//            threshold: findings are frozen per snapshot, so data recorded after
//            the snapshot was mined is provably not in it.
//   'age'  — the snapshot is at least STALE_SNAPSHOT_MAX_AGE_DAYS old. The
//            fallback when the data signal is unavailable (null latest_po_date)
//            or not tripped.
//
// Local-midnight comparison for both shapes so a date-only string and a
// timestamp are compared as calendar days (mirrors formatDate's TZ-safe
// handling — `new Date('2026-07-21')` is UTC midnight and can render/compare as
// the previous day west of Greenwich).
//
// Lane 694: accepts a Date as well as a string, because the two are different
// KINDS of input and were being converted differently. A stored date/timestamp
// is a written calendar date and is read as literally that. The clock is an
// INSTANT, and the only calendar that means anything to the person reading the
// page is their own — so its local fields are read directly.
//
// Routing the clock through `toISOString()` first (what this did until Lane 694)
// re-read that instant on the UTC calendar, which is a day AHEAD of the reader's
// once UTC has rolled over — after 17:00 in Los Angeles — and a day BEHIND it in
// the small hours east of Greenwich. That mixed the two bases against each other
// and made a 30-day-old briefing report "31 days old" every Pacific evening.
const toLocalDay = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? null
      : new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const s = String(value);
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

export const snapshotStaleness = ({ createdAt, latestPoDate, now = new Date() }) => {
  const snapDay = toLocalDay(createdAt);
  if (!snapDay) return null;                 // no date -> no claim
  const poDay = toLocalDay(latestPoDate);
  const today = toLocalDay(now);             // the READER's calendar day (Lane 694)
  const ageDays = today ? Math.floor((today - snapDay) / MS_PER_DAY) : null;

  if (poDay && snapDay < poDay) {
    return { kind: 'data', ageDays, latestPoDate };
  }
  if (ageDays !== null && ageDays >= STALE_SNAPSHOT_MAX_AGE_DAYS) {
    return { kind: 'age', ageDays, latestPoDate: null };
  }
  return null;
};

// Elapsed-time label for the in-flight regenerate ("45s", "2m 15s"). Honest
// progress: a multi-minute wait with a frozen "Generating..." reads as hung.
export const elapsedLabel = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  if (total < 60) return `${total}s`;
  return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, '0')}s`;
};

// The design §5 honesty guard. The dollar column shows the NUMBER only
// (Wave-1 item 3, 2026-07-19): the family word no longer renders as a visible
// sub-label — the family meaning lives in the Type column (FAMILY_LABELS) and
// the cell's hover title. The number stays honest: volume keeps its "/yr"
// suffix (prospective), observed/lead-time carry none, and the Type column
// disambiguates every row. Lane 148: the padded lead-time dollar is a
// working-capital figure (cash tied up in early-ordered stock) — its own word
// in the tooltip, never "overpaid".
const headlineMoney = (familyType, v) => (familyType === 'volume' ? `${formatUsd(v)}/yr` : formatUsd(v));
const headlineWord = (familyType) => {
  if (familyType === 'volume') return 'opportunity';
  if (familyType === 'leadtime') return 'tied up in stock';
  return 'overpaid';
};
// Family-color class on the dollar (volume=accent, observed/lead-time=warn).
const oppToneClass = (familyType) => {
  if (familyType === 'volume') return 'vol';
  if (familyType === 'leadtime') return 'lt';
  return 'obs';
};

// ---------------------------------------------------------------------------
// Lane 353 (O-1, round-4 R6.6): proportionality context cards — display-only
// formatting of the endpoint's additive `context` echo. The ratio itself
// (spend_share_pct) arrives COMPUTED from the server (same basis discipline as
// every other figure on this page); these helpers only pick a display form.
// ---------------------------------------------------------------------------

// Share-of-spend display: >= 0.1% renders as a percent ("0.5%"); below that,
// basis points ("6 bps"); a sub-half-bp share renders "under 1 bp" — never a
// misleading "0 bps".
const shareLabel = (pct) => {
  if (pct >= 0.1) return `${pct.toFixed(1)}%`;
  const bps = pct * 100;
  return bps >= 0.5 ? `${Math.round(bps)} bps` : 'under 1 bp';
};

// Compact dollars for the card subline ("$45.8M") — display rounding only.
const compactUsd = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 10000) return formatUsd(value);
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  return `$${Math.round(n / 1e3)}K`;
};

// SAFE/WATCH/RISKY (inventory) map onto the shared good/warn/crit chip classes.
// (The lead-time ALIGNED/PADDED/UNDERSTATED chips were dropped with the "Lead
// time" column, QA-1 / decision 1.)
const CHIP_CLASS = { SAFE: 'safe', WATCH: 'watch', RISKY: 'risky' };
// disposition status -> status-pill class
const STATUS_CLASS = { pursuing: 'pursue', needs_data: 'needs', accepted: 'accept' };

function Chevron({ open }) {
  return (
    <svg
      width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ flex: 'none', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s ease' }}
    >
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" />
  </svg>
);

/**
 * A collapsed, claim-free section (Lane 47 §1C/§1E; Linear accordion): monitor
 * ("Already at your best price", green), price-gone ("Best price no longer
 * available", neutral — Lane 355) or verify ("Verify unit of measure", amber).
 * Rows carry material # / name / supplier (mirroring the ranked table,
 * rev-1) — this component never authors claim language and never renders a
 * dollar. Rows are only in the DOM when expanded (the collapse guard).
 *
 * Lane 379: the row cells carry `opp-`-PREFIXED class names on purpose. CRA
 * bundles every page stylesheet into one global sheet with no scoping, so a
 * bare cell class can collide with another page's scope ROOT — `.ct` (the old
 * name cell) is the Contracts root and carries `min-height:100vh`, which blew
 * every claim-free row up to a full viewport. Do not shorten these back.
 */
const SECTION_TONES = {
  success: { dot: 'var(--good)', fg: 'var(--good)', bg: 'var(--good-soft)' },
  // Lane 355: price-gone rows are informational evidence, not a data problem —
  // amber is already this section's "verify the data" tone, so reusing it would
  // blur two different meanings. Neutral/accent matches Lane 338's reading
  // ("evidence is not a warning").
  neutral: { dot: 'var(--accent)', fg: 'var(--accent)', bg: 'var(--accent-soft)' },
  warning: { dot: 'var(--warn)', fg: 'var(--warn)', bg: 'var(--warn-soft)' },
};

function ClassifiedSection({ label, explainer, tone, section, onRowClick, testId }) {
  const [open, setOpen] = useState(false);
  const rows = section?.rows || [];
  const count = section?.count || 0;
  if (count === 0) return null;

  const t = SECTION_TONES[tone] || SECTION_TONES.warning;
  const dotColor = t.dot;
  const countStyle = { color: t.fg, background: t.bg };

  return (
    <div data-testid={testId} className="opp-acc">
      <button type="button" className="opp-acc-h" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Chevron open={open} />
        <span className="opp-acc-dot" style={{ background: dotColor }} />
        <span className="at">{label}</span>
        <span className="cnt" style={countStyle}>{count}</span>
        <span className="as">{explainer}</span>
      </button>
      {open && rows.map((row) => (
        <div
          key={row.finding_id}
          className="opp-cfrow"
          role="button"
          tabIndex={0}
          onClick={() => onRowClick(row)}
          onKeyDown={(e) => { if (e.key === 'Enter') onRowClick(row); }}
        >
          <span className="opp-mno" title={row.material_number}>{row.material_number}</span>
          <span className="opp-cfnm" title={row.material_name || ''}>{row.material_name || 'Not available'}</span>
          <span className="opp-cfsup" title={row.supplier_display}>{row.supplier_display}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Lane 338 (R3-OPP-STEP): the claim-free SUPPLIER DISCUSSION section — the
 * step-change family (always CLASS_EVIDENCE, Lane 323) reframed OUT of the
 * savings-opportunity framing per Ryan's round-3 QA: a sustained price step-up
 * is not an "opportunity" (there is nothing to DO on it); it is a talking
 * point for the next supplier conversation. Its own section — never mixed
 * into the ranked savings rows, never a dollar (the endpoint's evidence rows
 * carry NO dollar field; jest-locked). Modeled on ClassifiedSection, plus the
 * engine-authored question rendered verbatim as the talking point per row.
 * Absent/empty `evidence` key (flag-OFF / pre-lane snapshots) renders nothing.
 *
 * Lane 389 (Ryan staging QA): "what's the total spend? ... how much do these
 * step-ups cost us? ... some of them are more important than others." Each row
 * now carries its SCALE CONTEXT — the cell's trailing-12mo spend and what the
 * higher level adds per year at that volume — plus a section total, and the
 * backend orders the rows by that annual effect. STILL CLAIM-FREE: these are
 * the engine's own FROZEN evidence figures (detail_json.math, echoed verbatim
 * — the UI computes no dollar except the visible-row total), they are never
 * called savings/opportunity/recoverable, they stay out of the hero total, the
 * ranked table and the family chips. A row whose snapshot predates the frozen
 * figures carries no `step` key and renders EXACTLY as it did before (the
 * pre-Lane-389 3-column layout, no header, no dollar) — jest-pinned.
 */
function DiscussionSection({ section, onRowClick }) {
  const [open, setOpen] = useState(false);
  const rows = section?.rows || [];
  const count = section?.count || 0;
  if (count === 0) return null;

  // Scale context renders only when the frozen figures are actually present.
  const ctxRows = rows.filter((r) => r.step);
  const hasCtx = ctxRows.length > 0;
  // The ONE piece of arithmetic on this surface: a sum over exactly the rows
  // rendered below, so the header can never disagree with what you see when
  // you expand it. No re-derivation — each addend is a frozen engine value.
  const sumOf = (key) => ctxRows.reduce((acc, r) => acc + (Number(r.step[key]) || 0), 0);
  const totalSpend = hasCtx ? sumOf('spend_12mo') : null;
  const totalEffect = hasCtx ? sumOf('annual_effect') : null;

  return (
    <section className="opp-sec" data-testid="discussion-section">
      <div className="opp-sec-h">
        <span className="opp-lbl">Supplier discussion points</span>
        <span className="note">evidence to raise in your next supplier conversation, not savings claims</span>
      </div>
      <div className="opp-acc">
        <button type="button" className="opp-acc-h" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          <Chevron open={open} />
          <span className="opp-acc-dot" style={{ background: 'var(--accent)' }} />
          <span className="at">Sustained price step-ups</span>
          <span className="cnt" style={{ color: 'var(--accent)', background: 'var(--accent-soft)' }}>{count}</span>
          <span className="as">The price moved to a new level and stayed there. Worth putting in front of the supplier - no savings are claimed.</span>
        </button>
        {hasCtx && (
          <div className="opp-discsum" data-testid="discussion-scale">
            <span className="opp-discsum-k">Spend on these, last 12 months</span>
            <span className="opp-discsum-v">{formatUsd(totalSpend)}</span>
            <span className="opp-discsum-sep" aria-hidden="true" />
            <span className="opp-discsum-k">The step-ups add, per year at that volume</span>
            <span className="opp-discsum-v">{formatUsd(totalEffect)}</span>
          </div>
        )}
        {open && hasCtx && (
          <div className="opp-cfrow disc ctx opp-dischead" aria-hidden="true">
            <span className="opp-mno">Material</span>
            <span className="opp-cfnm">Name</span>
            <span className="opp-cfsup">Supplier</span>
            <span className="opp-cfspend">Spend (12mo)</span>
            <span className="opp-cfeff">Adds / yr</span>
          </div>
        )}
        {open && rows.map((row) => (
          <div
            key={row.finding_id}
            className={`opp-cfrow disc${hasCtx ? ' ctx' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => onRowClick(row)}
            onKeyDown={(e) => { if (e.key === 'Enter') onRowClick(row); }}
          >
            <span className="opp-mno" title={row.material_number}>{row.material_number}</span>
            <span className="opp-cfnm" title={row.material_name || ''}>{row.material_name || 'Not available'}</span>
            <span className="opp-cfsup" title={row.supplier_display}>{row.supplier_display}</span>
            {hasCtx && (
              <span className="opp-cfspend" title="Your spend with this supplier on this material over the last 12 months">
                {row.step ? formatUsd(row.step.spend_12mo) : 'Not available'}
              </span>
            )}
            {hasCtx && (
              <span className="opp-cfeff" title="What the new price level adds per year at your last-12-months volume - evidence, not a savings claim">
                {row.step ? formatUsd(row.step.annual_effect) : 'Not available'}
              </span>
            )}
            {row.ai_question && <span className="opp-cfq">{row.ai_question}</span>}
          </div>
        ))}
      </div>
    </section>
  );
}

// One ranked-findings row. Kept out of the main component so the conditional
// inventory / lead-time cells stay readable.
function FindingRow({ row, showInventory, onClick }) {
  const { family_type: family } = row;
  const disp = row.disposition;
  const chipCell = (block) => {
    if (family !== 'volume') return <td />;                 // Family-2: not applicable
    if (!block || !block.chip) return <td><span className="opp-chip none">None</span></td>;
    return <td title={block.reason || ''}><span className={`opp-chip ${CHIP_CLASS[block.chip] || ''}`}>{block.chip}</span></td>;
  };
  return (
    <tr className="click" tabIndex={0} onClick={() => onClick(row)}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(row); }}>
      <td className="opp-rk num">{row.rank}</td>
      <td className="opp-mno">
        {/* Material number is a blue link into the material dossier (Wave-1
            item 3). stopPropagation on click + keydown so activating the link
            opens the dossier without also firing the row's go-to-finding. */}
        <Link
          to={`/materials/${row.material_number}`}
          className="opp-mlink"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {row.material_number}
        </Link>
      </td>
      <td className="opp-mt">{row.material_name || 'Not available'}</td>
      <td className="opp-supcell">{row.supplier_display}</td>
      <td className="opp-type">
        {FAMILY_LABELS[family] || family}
        {/* Both incident-dated families surface the date next to the type
            (same_day = the day; spike_reversion = the excursion date, the
            recovery-viability signal per design §7). Lane 258. */}
        {(family === 'same_day' || family === 'spike_reversion') && row.incident_date && (
          <span className="dt">{formatDate(row.incident_date)}</span>
        )}
      </td>
      <td className="right">
        <div className={`opp-opp ${oppToneClass(family)}`}
          title={`${headlineMoney(family, row.headline_value)} ${headlineWord(family)}`}>
          <b>{headlineMoney(family, row.headline_value)}</b>
          {row.net_value !== null && row.net_value !== undefined && (
            <span className="net">~{formatUsd(row.net_value)} net of carrying cost</span>
          )}
        </div>
      </td>
      {showInventory && chipCell(row.inventory)}
      <td>
        {!disp ? (
          <span className="opp-st open">Undecided</span>
        ) : (
          <>
            <span className={`opp-st ${STATUS_CLASS[disp.status] || 'open'}`}>
              {DISPOSITION_LABELS[disp.status] || disp.status}
            </span>
            {disp.changed_since_accepted && (
              <span className="opp-st changed">
                {`Changed ${formatUsd(disp.headline_at_disposition)} → ${formatUsd(row.headline_value)}`}
              </span>
            )}
          </>
        )}
      </td>
    </tr>
  );
}

function Opportunities() {
  const navigate = useNavigate();
  const { token } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [moduleUnavailable, setModuleUnavailable] = useState(false);

  const [snapshotSel, setSnapshotSel] = useState('latest');
  const [family, setFamily] = useState('');
  const [status, setStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // Lane 842: the ticked warehouses. [] = nothing ticked = today's behaviour,
  // byte-identical (the server adds no predicate at all for an empty list).
  const [warehouses, setWarehouses] = useState([]);
  // Lane 851: is the warehouse dropdown open? Shape only — `warehouses` above
  // is the filter's whole state and this touches none of it.
  const [warehouseOpen, setWarehouseOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const [regenBusy, setRegenBusy] = useState(false);
  const [regenNotice, setRegenNotice] = useState(null);
  // Lane 431: honest progress for a multi-minute regenerate. A frozen
  // "Generating..." across 8 minutes reads as hung, which is what sent Ryan
  // back to click a second time.
  const [regenElapsedMs, setRegenElapsedMs] = useState(0);

  // Lane 377: refs so an in-flight regenerate poll stops cleanly on unmount
  // (no setState after teardown, no orphaned timer).
  const mountedRef = useRef(true);
  const pollTimerRef = useRef(null);
  useEffect(() => () => {
    mountedRef.current = false;
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
  }, []);

  // Lane 851: close the warehouse dropdown on click-outside / Escape. Same
  // shape as the house popover (components/MetricInfo.js): `mousedown` so the
  // close lands before a click on whatever was underneath, and a ref-contains
  // check so ticking a box inside does NOT close it — this is multi-select,
  // it has to stay open while you tick.
  const warehouseRef = useRef(null);
  useEffect(() => {
    if (!warehouseOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setWarehouseOpen(false); };
    const onDown = (e) => {
      if (warehouseRef.current && !warehouseRef.current.contains(e.target)) {
        setWarehouseOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [warehouseOpen]);

  // Lane 431: tick the elapsed counter once a second while a regenerate is in
  // flight. Interval is torn down when the regenerate settles or the page
  // unmounts, so it can never outlive the work it describes.
  useEffect(() => {
    if (!regenBusy) return undefined;
    const startedAt = Date.now();
    setRegenElapsedMs(0);
    const t = setInterval(() => setRegenElapsedMs(Date.now() - startedAt), 1000);
    return () => clearInterval(t);
  }, [regenBusy]);

  // Debounce the search box into the fetch param.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('snapshot', snapshotSel);
      if (family) params.set('family', family);
      if (status) params.set('status', status);
      if (search) params.set('search', search);
      // Lane 842: one repeated `warehouse` param per ticked box — the server
      // ORs across them (a finding surfaces if ANY of its purchases sits at ANY
      // ticked warehouse). append(), not set(), so all of them survive.
      warehouses.forEach((w) => params.append('warehouse', w));
      const res = await fetch(`${API_BASE}/api/opportunities?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 404) {
        // Flag flipped OFF mid-session — quiet state, not an error.
        setModuleUnavailable(true);
        setData(null);
        return;
      }
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setModuleUnavailable(false);
      setData(await res.json());
      setShowAll(false);   // a fresh list re-folds
    } catch (err) {
      console.error('Opportunities fetch failed:', err.message);
      setError('Unable to load opportunities. Please try again.');
    } finally {
      setLoading(false);
    }
    // `warehouses` only gets a new array identity when a box is actually
    // toggled, so it re-fetches on a tick and never on an unrelated re-render.
  }, [token, snapshotSel, family, status, search, warehouses]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Lane 377: the newest snapshot id the page already knows about — the
  // baseline a successful regenerate must exceed. Read from held state (the
  // list the page already holds), so no extra fetch is needed at click time.
  const knownLatestSnapshotId = () => {
    const ids = (data?.snapshots || [])
      .map((s) => Number(s.snapshot_id))
      .filter(Number.isFinite);
    const cur = Number(data?.snapshot?.snapshot_id);
    if (Number.isFinite(cur)) ids.push(cur);
    return ids.length ? Math.max(...ids) : null;
  };

  // Lane 377: one read of the latest complete snapshot's id (null on any
  // non-ok / malformed response — treated as "no answer yet, keep polling").
  const fetchLatestSnapshotId = async () => {
    const res = await fetch(`${API_BASE}/api/opportunities?snapshot=latest`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const body = await res.json();
    const id = Number(body?.snapshot?.snapshot_id);
    return Number.isFinite(id) ? id : null;
  };

  // Lane 377: poll the latest snapshot. A snapshot id newer than the baseline
  // means the regenerate actually committed (the request just outlived the
  // proxy). Returns false only if the window elapses with no newer snapshot —
  // that is a genuine failure. Stops early on unmount.
  // Lane 431: window raised 120s -> 15min (measured 7m44s generation, see the
  // constant), with the interval backing off after the first minute.
  const pollForNewerSnapshot = async (baselineId) => {
    const startedAt = Date.now();
    const deadline = startedAt + REGEN_POLL_MAX_MS;
    while (Date.now() < deadline) {
      const interval = (Date.now() - startedAt) < REGEN_POLL_BACKOFF_AFTER_MS
        ? REGEN_POLL_INTERVAL_MS
        : REGEN_POLL_INTERVAL_LATE_MS;
      await new Promise((resolve) => {
        pollTimerRef.current = setTimeout(resolve, interval);
      });
      if (!mountedRef.current) return false;
      let latestId = null;
      try {
        latestId = await fetchLatestSnapshotId();
      } catch (e) {
        latestId = null; // transient poll error — keep waiting
      }
      if (!mountedRef.current) return false;
      if (latestId !== null && (baselineId === null || latestId > baselineId)) {
        return true;
      }
    }
    return false;
  };

  const handleRegenerate = async () => {
    setRegenBusy(true);
    setRegenNotice(null);
    // Capture the baseline BEFORE the POST, from the state the page holds.
    const baselineId = knownLatestSnapshotId();
    try {
      const res = await fetch(`${API_BASE}/api/opportunities/regenerate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 404) {
        setModuleUnavailable(true);
        return;
      }
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const body = await res.json();
      if (body.status === 'already_running') {
        setRegenNotice('A briefing is already being generated. It will use the latest data - check back in a moment.');
      } else if (snapshotSel !== 'latest') {
        setSnapshotSel('latest'); // effect refetches
      } else {
        await fetchList();
      }
    } catch (err) {
      // The request failed OR (far more often, on staging behind a ~100s proxy)
      // outlived the proxy while the inline AI-prose upgrade was still running.
      // Don't declare failure yet — regenerate usually commits its snapshot
      // anyway. Poll for a newer snapshot; only a truly-failed regenerate leaves
      // the latest snapshot unchanged through the whole window.
      console.error('Regenerate request did not return cleanly; polling for a committed snapshot:', err.message);
      const committed = await pollForNewerSnapshot(baselineId);
      if (!mountedRef.current) return;
      if (committed) {
        setRegenNotice('Your briefing was regenerated.');
        if (snapshotSel !== 'latest') {
          setSnapshotSel('latest'); // effect refetches
        } else {
          await fetchList();
        }
      } else {
        setRegenNotice('Could not generate a new briefing. Please try again.');
      }
    } finally {
      if (mountedRef.current) setRegenBusy(false);
    }
  };

  const topbar = (
    <div className="opp-topbar">
      <div className="opp-crumb"><span className="cur">Opportunities</span></div>
    </div>
  );

  if (moduleUnavailable) {
    return (
      <div className="opp">
        {topbar}
        <div className="opp-wrap">
          <div className="opp-hero empty" style={{ marginTop: '24px' }}>
            <div className="et">This module is not enabled</div>
            <div className="ed">The Opportunities module is currently switched off for this environment.</div>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="opp">
        {topbar}
        <div className="opp-wrap">
          <header className="opp-head"><div><h1 className="opp-h1">Opportunities</h1></div></header>
          <div className="opp-empty" style={{ marginTop: '24px' }}>Loading opportunities...</div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="opp">
        {topbar}
        <div className="opp-wrap">
          <header className="opp-head"><div><h1 className="opp-h1">Opportunities</h1></div></header>
          <div className="opp-empty" style={{ marginTop: '24px' }}>
            <div className="et">{error}</div>
            <button type="button" className="opp-btn pri" style={{ marginTop: '14px' }} onClick={fetchList}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const snapshot = data?.snapshot || null;
  const history = data?.snapshots || [];
  const findings = data?.findings || [];
  const familySummaries = data?.family_summaries || [];
  const monitorAll = data?.monitor || { count: 0, rows: [] };
  const verify = data?.verify || { count: 0, rows: [] };
  // Lane 355 (O-2): CLASS_MONITOR carries TWO flavors since Lane 352 and one
  // heading is false for one of them — a buyer whose price nearly tripled must
  // never be told "already at your best price". Split on the server-echoed
  // `price_gone` discriminator (detail_json.price_gone vs .suppression).
  // Display-only: the rows themselves are untouched, and a pre-Lane-352
  // snapshot carries neither key -> price_gone false on every row -> the
  // at-best section renders EXACTLY as it does today (byte-identity pin).
  const monitorRowsAll = monitorAll.rows || [];
  const monitorAtBestRows = monitorRowsAll.filter((r) => !r.price_gone);
  const monitorGoneRows = monitorRowsAll.filter((r) => r.price_gone);
  const monitorAtBest = { count: monitorAtBestRows.length, rows: monitorAtBestRows };
  const monitorGone = { count: monitorGoneRows.length, rows: monitorGoneRows };
  // Lane 338: step-change evidence rows (claim-free supplier discussion
  // points). Absent key (older API / no evidence rows) -> count 0 -> nothing.
  const evidence = data?.evidence || { count: 0, rows: [] };
  // Lane 353 (O-1): proportionality context. Absent key (older API) or null
  // fields (server fail-soft) -> the matching card is omitted entirely.
  const context = data?.context || null;
  const showPoCard = Boolean(context && context.po_universe_count > 0);
  // The share card needs BOTH a real denominator and a nonzero findings total
  // (a 0-findings snapshot renders the PO card alone — a share of nothing
  // isn't a useful ratio).
  // Lane 384: the card labels its own window from the server, so the window is
  // an INPUT like the others — without it the card would read "undefined-month
  // spend". Same honest-absence rule: a missing input omits the card.
  const showShareCard = Boolean(
    context && context.spend_24mo > 0
    && context.spend_window_months > 0
    && context.spend_share_pct !== null && context.spend_share_pct !== undefined
    && data?.total_opportunity > 0,
  );
  // Lane 842: the warehouse ticks are options the server derives from THIS
  // snapshot's findings, so every box can return at least one row. The list is
  // computed ignoring every filter (including the ticks themselves), so it does
  // not reshuffle while you use it.
  const warehouseOptions = data?.warehouses?.options || [];
  // Lane 851: the closed dropdown's label, phrased as a sibling of the two
  // selects beside it ("All types" / "Active (accepted hidden)"). One tick
  // names the site, because that is more use than a count of one.
  const warehouseLabel = warehouses.length === 0
    ? 'All warehouses'
    : (warehouses.length === 1 ? warehouses[0] : `${warehouses.length} warehouses`);
  // Lane 842: `warehouses` MUST be in hasFilters. Without it a tick that
  // matches nothing would print "Nothing above the materiality floor was found
  // in this snapshot" — which is false, and on a live PROD surface.
  const hasFilters = Boolean(family || status || search || warehouses.length);

  // Lane 431: is the briefing being viewed out of date? Evaluated for whichever
  // snapshot is on screen — a deliberately-picked historical snapshot IS old,
  // and saying so is honest rather than nagging (the picker says which one; this
  // says what that means). Absent inputs -> no claim.
  const staleness = snapshot
    ? snapshotStaleness({
      createdAt: snapshot.created_at,
      latestPoDate: context?.latest_po_date || null,
    })
    : null;

  // Lane 73: the display-only inventory signal column renders ONLY when at least
  // one row carries a block, so pre-lane / flag-OFF snapshots show the exact same
  // table as today. (QA-1 / decision 1: the sibling lead-time chip column was
  // cut — its story is owned by the Delivery Reliability surface.)
  const showInventory = findings.some((f) => f.inventory);

  // Display shaping only: rank annotation + the client-side fold. The list
  // arrives ranked from the server; nothing is re-ordered or recomputed.
  const ranked = findings.map((f, i) => ({ ...f, rank: i + 1 }));
  const visible = showAll ? ranked : ranked.slice(0, FOLD_COUNT);
  const foldedCount = ranked.length - visible.length;

  const goToFinding = (row) => navigate(`/opportunities/${row.finding_id}`);

  // Lane 431: the button carries the elapsed time so a multi-minute run visibly
  // progresses instead of sitting on a frozen "Generating...". A factory rather
  // than a single element because the staleness strip renders its own copy, and
  // two buttons with identical text are ambiguous to query (and to a screen
  // reader) — each callsite passes its own testid + accessible label.
  const renderRegenerateButton = (testId, label = 'Regenerate') => (
    <button
      type="button" className="opp-btn pri" onClick={handleRegenerate}
      disabled={regenBusy || !token}
      data-testid={testId}
      aria-label={regenBusy ? 'Generating briefing' : label}
    >
      {regenBusy ? `Generating... ${elapsedLabel(regenElapsedMs)}` : label}
    </button>
  );
  const regenerateButton = renderRegenerateButton('opp-regenerate');

  // Lane 431: while it runs, say how long it takes. Regenerating rebuilds every
  // detector family over the full purchase history and then rewrites each
  // finding's prose — measured at ~8 minutes on the real XMEK dataset — so a
  // reader who is not told that reasonably concludes it has hung.
  const regenProgress = regenBusy ? (
    <div className="opp-banner progress" data-testid="opp-regen-progress">
      <b>Generating your briefing: {elapsedLabel(regenElapsedMs)} elapsed.</b>{' '}
      This rebuilds every finding from your full purchase history and usually takes
      several minutes. You can leave this page open; the results appear here when it
      finishes.
    </div>
  ) : null;

  return (
    <div className="opp">
      {topbar}
      <div className="opp-wrap">
        {regenNotice && <div className="opp-banner">{regenNotice}</div>}
        {regenProgress}

        {!snapshot ? (
          <>
            <header className="opp-head">
              <div className="grow">
                <h1 className="opp-h1">Opportunities</h1>
                <div className="opp-sub">Savings found in your purchase history, ranked by dollar impact.</div>
              </div>
            </header>
            <div className="opp-hero empty">
              <div className="et">No briefing has been generated yet</div>
              <div className="ed">
                Generate your first opportunities briefing from your current purchase history.
                {/* Lane 431: "a few seconds" was measured at ~8 minutes on the
                    real XMEK dataset. Setting the expectation honestly here is
                    the same fix as the in-flight progress banner. */}
                {' '}It rebuilds every finding from scratch and usually takes several minutes.
              </div>
              {regenerateButton}
            </div>
          </>
        ) : (
          <>
            {/* Header — title + snapshot picker + Regenerate. */}
            <header className="opp-head">
              <div className="grow">
                <h1 className="opp-h1">Opportunities</h1>
                <div className="opp-sub">Savings found in your purchase history, ranked by dollar impact.</div>
              </div>
              <div className="opp-filters">
                <div>
                  <label className="opp-field-lbl" htmlFor="opp-snapshot">Snapshot</label>
                  <select
                    id="opp-snapshot" className="opp-sel" value={snapshotSel}
                    onChange={(e) => {
                      setSnapshotSel(e.target.value);
                      // Lane 842: a different snapshot offers a different set
                      // of warehouses. Carrying the ticks over could leave one
                      // ACTIVE but with no checkbox to see or untick it — an
                      // invisible filter. Clearing on an explicit snapshot
                      // change is the honest reset.
                      setWarehouses([]);
                    }}
                  >
                    <option value="latest">Latest</option>
                    {history.map((s) => (
                      <option key={s.snapshot_id} value={String(s.snapshot_id)}>
                        {`${formatDate(s.created_at)} (${pluralize(s.finding_count, 'finding')})`}
                      </option>
                    ))}
                  </select>
                </div>
                {regenerateButton}
              </div>
            </header>

            {/* Lane 431: the out-of-date warning. Sits ABOVE the hero on
                purpose — it must be read before the numbers it qualifies, not
                as a footnote under them (the pre-existing "As of" line below is
                exactly that footnote, and it did not register). States the
                conclusion and the reason; the action is the same Regenerate
                button. Renders nothing when the briefing is current. */}
            {staleness && (
              <section className="opp-stale" data-testid="opp-stale" data-stale-kind={staleness.kind}>
                <div className="opp-stale-h">
                  {staleness.kind === 'data'
                    ? 'This briefing is out of date'
                    : `This briefing is ${pluralize(staleness.ageDays, 'day')} old`}
                </div>
                <div className="opp-stale-b">
                  {staleness.kind === 'data' ? (
                    <>
                      It was generated <b>{formatDate(snapshot.created_at)}</b>, before purchase
                      data you now have on file (through <b>{formatDate(staleness.latestPoDate)}</b>).
                      Those purchases are not in the findings below. Regenerate to include them.
                    </>
                  ) : (
                    <>
                      It was generated <b>{formatDate(snapshot.created_at)}</b>. The findings below
                      are frozen as of that date. Regenerate to rebuild them from your current
                      purchase history.
                    </>
                  )}
                </div>
                {renderRegenerateButton('opp-stale-regenerate', 'Regenerate now')}
              </section>
            )}

            {/* Hero / summary strip. */}
            <section className="opp-hero">
              <div className="k">Total opportunity &middot; this snapshot</div>
              <div className="big">
                {formatUsd(data.total_opportunity)}
                <small>({pluralize(data.finding_count, 'finding')})</small>
              </div>
              <div className="asof">
                As of <b>{formatDate(snapshot.created_at)}</b> &middot; {triggerSourceLabel(snapshot.trigger_source)}
              </div>
              {familySummaries.length > 0 && (
                <div className="opp-fams">
                  {familySummaries.map((fs) => (
                    <div className="opp-fam-chip" key={fs.family_type}>
                      <span className={`fdot ${fs.family_type === 'volume' ? 'vol' : ''}`} />
                      <span className="fn">{FAMILY_LABELS[fs.family_type] || fs.family_type}</span>
                      <span className="fv">{formatUsd(fs.total)}</span>
                      <span className="fc">{pluralize(fs.count, 'finding')}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Lane 353 (O-1, R6.6): proportionality context — two quiet
                  scale call-outs, subordinate to the total + family chips.
                  Every number renders verbatim from the response: the finding
                  count + findings dollar are the SAME fields as the hero above
                  (reconcile by construction); the ratio arrives server-
                  computed. A card whose inputs are missing is omitted —
                  honest absence, never a wrong ratio.
                  Lane 384 (2026-07-25, Ryan staging QA): BOTH cards now name
                  the SAME window — the share card's denominator moved to the
                  24-month span the findings are mined over, and each card
                  labels its basis from its OWN server field
                  (po_window_months / spend_window_months) rather than a
                  hardcoded phrase, so a window change can never leave one card
                  quietly claiming the other's basis. */}
              {(showPoCard || showShareCard) && (
                <div className="opp-ctx" data-testid="context-cards">
                  {showPoCard && (
                    <div className="opp-ctx-card" data-testid="context-po-card">
                      <span className="cv">
                        {`${pluralize(data.finding_count, 'finding')} across ${pluralize(context.po_universe_count, 'PO')}`}
                      </span>
                      <span className="cs">
                        {`last ${context.po_window_months} months of purchase history`}
                      </span>
                    </div>
                  )}
                  {showShareCard && (
                    <div className="opp-ctx-card" data-testid="context-share-card">
                      <span className="cv">
                        {`${shareLabel(context.spend_share_pct)} of ${context.spend_window_months}-month spend`}
                      </span>
                      <span className="cs">
                        {`${formatUsd(data.total_opportunity)} in findings vs ${compactUsd(context.spend_24mo)} purchased`}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* Filter bar — family / status / debounced search / warehouse
                (all server-side). */}
            <div className="opp-fbar">
              <select className="opp-sel" aria-label="Filter by type" value={family} onChange={(e) => setFamily(e.target.value)}>
                {FAMILY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select className="opp-sel" aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>

              {/* Lane 851: the warehouse filter, as a DROPDOWN sitting in the
                  filter bar beside the two selects — Ryan: "I didn't want there
                  to be a million fucking buttons to click. I just wanted it to
                  be a drop down... but you'd have tick boxes next to the
                  filtered ones." (Lane 842 shipped it as a wrapping row of
                  checkbox buttons under the bar, which is what he is reacting
                  to.) The trigger carries `opp-sel` verbatim, so it is the same
                  control as its two neighbours by construction rather than by a
                  copied set of values that can drift apart later.

                  BEHAVIOUR IS UNCHANGED from Lane 842 and must stay that way:
                  the same `warehouses` state, the same option-ordered toggle,
                  the same one-repeated-param-per-tick request, and the figures
                  above the list still do not move. This is the control's shape
                  only. Renders nothing at all when the snapshot yields no
                  options, so a tenant with no site data sees no filter. */}
              {warehouseOptions.length > 0 && (
                <div className="opp-msel-wrap" data-testid="warehouse-filter" ref={warehouseRef}>
                  <button
                    type="button" className="opp-sel opp-msel"
                    aria-haspopup="true" aria-expanded={warehouseOpen}
                    onClick={() => setWarehouseOpen((o) => !o)}
                  >
                    {warehouseLabel}
                  </button>
                  {warehouseOpen && (
                    <div className="opp-msel-pop" role="group" aria-label="Warehouse">
                      <div className="opp-msel-list">
                        {warehouseOptions.map((w) => {
                          const checked = warehouses.includes(w);
                          return (
                            <label key={w} className="opp-msel-opt">
                              <input
                                type="checkbox" checked={checked}
                                onChange={() => setWarehouses(
                                  checked
                                    ? warehouses.filter((x) => x !== w)
                                    // Preserve the option order so the outgoing
                                    // params (and the server's echo) are stable.
                                    : warehouseOptions.filter(
                                      (x) => x === w || warehouses.includes(x)),
                                )}
                              />
                              {w}
                            </label>
                          );
                        })}
                      </div>
                      <div className="opp-msel-foot">
                        {warehouses.length > 0 && (
                          <button
                            type="button" className="opp-msel-clear"
                            onClick={() => setWarehouses([])}
                          >
                            Clear
                          </button>
                        )}
                        {/* The honest one-liner, unchanged wording from Lane
                            842. It lives inside the dropdown now: that is where
                            the decision is made, and keeping it under the bar is
                            exactly the page clutter Ryan objected to. A finding
                            pools the purchases from every warehouse it was
                            bought at, so this filters WHICH findings are listed
                            — it does not re-cut their dollar figures, and the
                            totals above are unchanged on purpose. */}
                        <div className="opp-msel-note">
                          Shows findings with at least one purchase at the ticked warehouses. Dollar
                          figures still cover all warehouses, and the totals above never change.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="opp-search">
                <SearchIcon />
                <input
                  className="opp-input" placeholder="Search material or supplier"
                  value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
            </div>

            {/* Ranked findings. */}
            {loading ? (
              <div className="opp-empty">Loading opportunities...</div>
            ) : error ? (
              <div className="opp-empty">
                <div className="et">{error}</div>
                <button type="button" className="opp-btn pri" style={{ marginTop: '14px' }} onClick={fetchList}>Try again</button>
              </div>
            ) : findings.length === 0 ? (
              <div className="opp-empty">
                <div className="et">{hasFilters ? 'No findings match the current filters' : 'No opportunities in this snapshot'}</div>
                <div>{hasFilters
                  ? 'Try clearing the filters or the search box.'
                  : 'Nothing above the materiality floor was found in this snapshot. Accepted findings are hidden from this view unless they grow materially.'}
                </div>
              </div>
            ) : (
              <>
                <table className="opp-tbl">
                  <thead>
                    <tr>
                      <th className="num">#</th>
                      <th>Material</th>
                      <th>Name</th>
                      <th>Supplier</th>
                      <th>Type</th>
                      <th className="right">Opportunity</th>
                      {showInventory && <th>Inventory</th>}
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((row) => (
                      <FindingRow
                        key={row.finding_id}
                        row={row}
                        showInventory={showInventory}
                        onClick={goToFinding}
                      />
                    ))}
                  </tbody>
                </table>
                {foldedCount > 0 && (
                  <button type="button" className="opp-showall" onClick={() => setShowAll(true)}>
                    {`Show all ${ranked.length} findings`}
                    <span className="more">&middot; {foldedCount} more below</span>
                  </button>
                )}
              </>
            )}

            {/* Lane 338: supplier discussion points — its own section, OUTSIDE
                the savings-opportunity framing (and outside "Also found"
                below): these are talking points, not findings to act on. */}
            <DiscussionSection section={evidence} onRowClick={goToFinding} />

            {/* Claim-free classified sections (collapsed by default). */}
            <section className="opp-sec">
              <div className="opp-sec-h">
                <span className="opp-lbl">Also found, no dollar claim</span>
                <span className="note">honest signals that aren&rsquo;t savings plays</span>
              </div>
              <ClassifiedSection
                label="Already at your best price"
                explainer="No action needed - you are buying these at your 12-month best. They resurface if prices drift up."
                tone="success"
                section={monitorAtBest}
                onRowClick={goToFinding}
                testId="monitor-section"
              />
              {/* Lane 355 (O-2): the second monitor flavor (Lane 352 Rule-B) —
                  the cheap price is GONE. Its own heading, because the section
                  above would assert the literal opposite. Claim-free: no
                  dollar renders here either. */}
              <ClassifiedSection
                label="Best price no longer available"
                explainer="You paid less for these in the past, but that price has not come back. No savings are claimed - worth raising with the supplier."
                tone="neutral"
                section={monitorGone}
                onRowClick={goToFinding}
                testId="monitor-gone-section"
              />
              <ClassifiedSection
                label="Verify unit of measure"
                explainer="Extreme price spreads that usually mean a data issue, not a price opportunity. No savings claimed."
                tone="warning"
                section={verify}
                onRowClick={goToFinding}
                testId="verify-section"
              />
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export default Opportunities;
